import SEO from '../Components/SEO';
import Breadcrumb from '../Components/Breadcrumb';
import { ActivityLayout } from '../Components/activity-layout';

const guideImage = '/Images/teacher1-1280x470.webp';

const guideLevels = [
  {
    level: 'start',
    label: 'Getting Started',
    tagline: 'Chapter one',
cards: [
        {
          title: 'Know Your Students',
          image: '/Images/online-teacher.webp',
          desc: 'Understanding who your students are—their goals, backgrounds, and proficiency levels—is the foundation of effective English teaching.',
          links: [{ text: 'Read Guide', to: '/teach-english/know-your-students' }],
        },
        {
          title: 'Planning a Lesson',
          image: '/Images/1person2.webp',
          desc: 'A practical, modified PPP framework for online and classroom ESL lessons. Covers small talk, warmer, language review, trivia quiz, vocab, reading/video, and discussion questions — with A1–B1 vs B1+ timing breakdown.',
          links: [{ text: 'Read Guide', to: '/teach-english/planning-a-lesson' }],
        },
        {
          title: 'Brush Up on Your English',
          image: '/Images/tutor2-1280x470.webp',
          desc: 'Even experienced teachers need to keep their own English sharp. Target the grammar, vocabulary, and pronunciation that most affect classroom credibility.',
          links: [{ text: 'Read Guide', to: '/teach-english/brush-up-english' }],
        },
      ],
  },
  {
    level: 'plan',
    label: 'Planning Lessons',
    tagline: 'Chapter two',
cards: [
        {
          title: 'Build a Lesson Around a Topic',
          image: '/Images/2people1.webp',
          desc: 'Goal-driven lesson planning starts with a clear, measurable learning outcome and works backward. Learn to write aims that actually guide your activity choices.',
          links: [{ text: 'Read Guide', to: '/teach-english/build-lesson-around-topic' }],
        },
        {
          title: 'Sequence Grammar Points',
          image: '/Images/grammar1.webp',
          desc: 'Teaching grammar in a logical order prevents cognitive overload. This guide maps a principled sequence from A1 to C1 with rationale and prerequisite checks.',
          links: [{ text: 'Read Guide', to: '/teach-english/sequence-grammar-points' }],
        },
        {
          title: 'Recycle Vocabulary',
          image: '/Images/vocabulary.webp',
          desc: 'Students forget 70% of new words within 24 hours without deliberate recycling. Get a systematic framework for spaced repetition and productive retrieval.',
          links: [{ text: 'Read Guide', to: '/teach-english/recycle-vocabulary' }],
        },
      ],
  },
  {
    level: 'skills',
    label: 'Teaching the Four Skills',
    tagline: 'Chapter three',
cards: [
        {
          title: 'Reading',
          image: '/Images/reading.webp',
          desc: 'Effective reading lessons move learners from decoding to deep comprehension. Covers text selection, pre/while/post frameworks, and strategy instruction.',
          links: [{ text: 'Read Guide', to: '/teach-english/teaching-reading' }],
        },
        {
          title: 'Speaking',
          image: '/Images/speaking.webp',
          desc: 'Speaking is the skill students most want and often fear most. Covers fluency vs. accuracy balance, task design for real communication, and feedback that builds confidence.',
          links: [{ text: 'Read Guide', to: '/teach-english/teaching-speaking' }],
        },
        {
          title: 'Writing',
          image: '/Images/writing1-1280x470.webp',
          desc: 'Writing makes thinking visible. Walk through genre-based instruction, process writing stages, feedback that moves writers forward, and scaffolding for every CEFR level.',
          links: [{ text: 'Read Guide', to: '/teach-english/teaching-writing' }],
        },
      ],
  },
  {
    level: 'assess',
    label: 'Practice and Assessment',
    tagline: 'Chapter four',
cards: [
        {
          title: 'Quiz Your Students',
          image: '/Images/quiz1.webp',
          desc: 'Low-stakes quizzes boost retention, reveal gaps, and motivate without anxiety. Covers quiz design principles, question types, and ready-to-use templates.',
          links: [{ text: 'Read Guide', to: '/teach-english/quiz-students' }],
        },
        {
          title: 'Flashcard Drills',
          image: '/Images/flashcards1.webp',
          desc: 'Flashcards are the Swiss Army knife of vocabulary teaching. Covers card design principles, drill routines, digital vs. physical, and ready-made sets.',
          links: [{ text: 'Read Guide', to: '/teach-english/flashcard-drills' }],
        },
        {
          title: 'Watch and Discuss',
          image: '/Images/3people1a.webp',
          desc: 'Video is the most engaging input source—but only if tasks make viewing active. Covers video selection, pre/while/post frameworks, and discussion protocols.',
          links: [{ text: 'Read Guide', to: '/teach-english/watch-and-discuss' }],
        },
      ],
  },
];

const TeachEnglish = () => {
  return (
    <ActivityLayout
      title="How to Teach English"
      eyebrow="Teaching Guide"
      image={guideImage}
      intro="Teaching English online isn't about having perfect grammar or a fancy certificate. It's about showing up prepared, knowing your students, and running lessons that actually get people talking. This guide walks through the practical stuff — planning lessons that work, keeping your own English sharp, teaching the four skills without overcomplicating it, and simple ways to check progress. No jargon, just what works in real classrooms and video calls."
      seoContent={[
        'Start with the basics: figuring out who your student is, what they need, and where they\'re at. The first lesson is your chance to listen, take notes, and build the relationship that makes everything else easier. Then plan lessons around topics they care about, sequence grammar so it builds naturally, and recycle vocabulary so it actually sticks.',
        'The four skills don\'t need separate textbooks. Reading and video feed discussion. Writing grows from speaking. Quizzes, flashcards, and video tasks are just tools to keep things varied and give students a sense of progress. Pick what fits your student and skip the rest.'
      ]}
      levels={guideLevels}
    >
      <SEO
        title="How to Teach English Online: Practical Guide & Lesson Plans for ESL Teachers"
        description="Complete guide for online ESL teachers. Lesson planning frameworks, classroom management tips, and ready-to-use grammar, reading, speaking, vocabulary, and video resources organized by CEFR level. Free teaching materials included."
        name='English Exam Exercises'
        type='article'
        image={guideImage} />
      <Breadcrumb />
    </ActivityLayout>
  );
};

export default TeachEnglish;
